import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { WindowInformation } from '../../../services/window-information';
import { cameraService } from '../../../services/central-api/cameras';
import moment from 'moment';
import { Modal, View } from 'react-native';
import FormActions from '../../../components/FormActions';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import FormInput from '../../../components/FormInput';
import MyAppText from '../../../components/MyAppText';
import MyDropDownPicker from '../../../components/MyDropDownPicker';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    cameraId?: string;
}

const regex = /^[a-zA-Z0-9 +\-./:=_ãõẽíáàâêóôúüçÃÕẼÍÁÀÂÊÓÔÚÜÇ]*$/;

export default function DownloadModal({ cameraId, isModalVisible, setModalVisible }: ModalProps) {
    const { styles } = useStyles(styleSheet);

    const windowInfo = WindowInformation();

    const [startInDateFilter, setStartInDateFilter] = useState<Date | null>(null);
    const [startInHourFilter, setStartInHourFilter] = useState('00:00');
    const [endInDateFilter, setEndInDateFilter] = useState<Date | null>(null);
    const [endInHourFilter, setEndInHourFilter] = useState('00:00');
    const [downloadName, setDownloadName] = useState<string>('');
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [endInZIndex, setEndInZIndex] = useState<number>(15);

    const [isOpenTags, setIsOpenTags] = useState<boolean>(false);
    const [selectedTagsIds, setSelectedTagsIds] = useState<number[]>([]);
    const [selectableTags, setSelectableTags] = useState<{ label: string; value: number; }[]>([]);

    function closeDownloadModal() {
        setModalVisible(false);
        resetDownloadForm();
    }

    function resetDownloadForm() {
        setIsOpenTags(false);
        setDownloadName('');
        setSelectedTagsIds([]);
        setStartInDateFilter(null);
        setStartInHourFilter('00:00');
        setEndInDateFilter(null);
        setEndInHourFilter('00:00');
    }

    async function getDownloadTags() {
        try {
            const tags = await cameraService.getSimplifiedDownloadTags();
            setSelectableTags(tags.map((tag) => {
                return {
                    value: tag.id,
                    label: tag.name
                };
            }));
        } catch (err) {
            console.error(err);
        }
    }

    function getDates() {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(startInDateFilter, startInHourFilter ? `${startInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        return { beginIn, endIn };
    }

    function isDownloadFormValid() {
        const { beginIn, endIn } = getDates();

        return downloadName !== '' &&
            regex.test(downloadName) &&
            beginIn &&
            endIn;
    }

    async function createDownload() {
        try {
            if (!cameraId) {
                return;
            }
            setIsDownloading(true);

            const { beginIn, endIn } = getDates();

            await cameraService.createCameraDownload({
                cameraId: cameraId,
                name: downloadName,
                msStart: beginIn as number,
                msFinish: endIn as number,
                tags: selectedTagsIds
            });
            closeDownloadModal();
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('downloadStarted'),
            });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsDownloading(false);
        }
    }

    function validateDownloadName(value: string) {
        if (value == '') {
            return true;
        }
        return !regex.test(value);
    }

    useEffect(() => {
        if (isModalVisible) {
            getDownloadTags();
        }
    }, [isModalVisible]);

    return (
        <Modal visible={isModalVisible} animationType='fade' onRequestClose={() => closeDownloadModal()} transparent={true}>
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : {}]}>
                    <FormInput
                        label={translate('name')}
                        placeholder={translate('name')}
                        value={downloadName}
                        onChangeText={setDownloadName}
                        invalid={validateDownloadName}
                        errorMessage={translate('invalidCharacters')}
                    />
                    <View style={{ zIndex: 2 }}>
                        <View style={{ flex: 1, gap: 5 }}>
                            <MyAppText>{translate('tags')}</MyAppText>
                            <MyDropDownPicker
                                items={selectableTags}
                                multiple={true}
                                mode='BADGE'
                                value={selectedTagsIds}
                                setValue={setSelectedTagsIds}
                                setOpen={setIsOpenTags}
                                open={isOpenTags}
                            />
                        </View>
                    </View>
                    <View style={{ flexDirection: 'column', rowGap: 16, flexWrap: 'wrap', zIndex: endInZIndex != 15 ? 3 : 1, minWidth: 300 }}>
                        <FilterDatePicker
                            date={startInDateFilter}
                            onChangeDate={setStartInDateFilter}
                            time={startInHourFilter}
                            onChangeTime={setStartInHourFilter}
                            label={translate('beginIn')}
                            zIndex={16}
                        />
                        <FilterDatePicker
                            date={endInDateFilter}
                            onChangeDate={setEndInDateFilter}
                            time={endInHourFilter}
                            onChangeTime={setEndInHourFilter}
                            label={translate('endIn')}
                            zIndex={endInZIndex}
                            onCalendarOpen={() => setEndInZIndex(9999)}
                            onCalendarClose={() => setEndInZIndex(15)}
                        />
                    </View>
                    <View>
                        <FormActions
                            onSubmit={createDownload}
                            onClose={() => closeDownloadModal()}
                            disabled={!isDownloadFormValid() || isDownloading} />
                    </View>
                </View>
            </View>
        </Modal>);
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    }
}));
